import { PLATFORM_ROLE_LABELS, type PlatformPermission } from "@station/contracts";
import { Alert, Button, EmptyState, PRODUCT_NAME, Spinner, StatusBadge } from "@station/ui";
import { createFileRoute, Link, Outlet, redirect, useNavigate } from "@tanstack/react-router";
import {
  Activity,
  ArrowLeftRight,
  Building2,
  FileText,
  Gauge,
  Headset,
  LayoutDashboard,
  LogOut,
  Megaphone,
  Rocket,
  Settings,
  ShieldOff,
  Tags,
  Users,
  Wallet,
} from "lucide-react";
import type { ComponentType, ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { usePlatformMe } from "../features/platform/queries.js";
import { isApiError } from "../lib/errors.js";
import { bootstrapSession, logout, useMe } from "../lib/session.js";
import { StepUpProvider } from "../lib/step-up.js";

export const Route = createFileRoute("/platform")({
  beforeLoad: async ({ location }) => {
    if (!(await bootstrapSession())) throw redirect({ to: "/giris", search: { redirect: location.href } });
  },
  component: PlatformLayout,
});

interface NavItem {
  to: string;
  labelKey: string;
  icon: ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
  permission: PlatformPermission;
  exact?: boolean;
}

const NAV: NavItem[] = [
  {
    to: "/platform",
    labelKey: "platform.nav.overview",
    icon: LayoutDashboard,
    permission: "platform.tenants.read",
    exact: true,
  },
  {
    to: "/platform/kiracilar",
    labelKey: "platform.nav.tenants",
    icon: Building2,
    permission: "platform.tenants.read",
  },
  {
    to: "/platform/planlar",
    labelKey: "platform.nav.plans",
    icon: Tags,
    permission: "platform.catalog.read",
  },
  {
    to: "/platform/faturalar",
    labelKey: "platform.nav.invoices",
    icon: FileText,
    permission: "platform.billing.read",
  },
  {
    to: "/platform/tahsilat",
    labelKey: "platform.nav.payments",
    icon: Wallet,
    permission: "platform.billing.read",
  },
  {
    to: "/platform/devirler",
    labelKey: "platform.nav.transfers",
    icon: ArrowLeftRight,
    permission: "platform.tenants.read",
  },
  {
    to: "/platform/destek",
    labelKey: "platform.nav.support",
    icon: Headset,
    permission: "platform.tenants.read",
  },
  {
    to: "/platform/yayinlar",
    labelKey: "platform.nav.releases",
    icon: Rocket,
    permission: "platform.catalog.read",
  },
  {
    to: "/platform/saglik",
    labelKey: "platform.nav.health",
    icon: Activity,
    permission: "platform.health.read",
  },
  {
    to: "/platform/duyurular",
    labelKey: "platform.nav.announcements",
    icon: Megaphone,
    permission: "platform.catalog.read",
  },
  {
    to: "/platform/personel",
    labelKey: "platform.nav.staff",
    icon: Users,
    permission: "platform.catalog.read",
  },
  {
    to: "/platform/ayarlar",
    labelKey: "platform.nav.settings",
    icon: Settings,
    permission: "platform.catalog.read",
  },
];

/**
 * PLATFORM PANELİ yerleşimi (Prompt 24 web, ADR 0024 §1): kiracı menüsü YOKTUR — platform ekibinin kendi ekranları. Yetki
 * `GET /platform/me`'den (rol → izinler); personel olmayan kullanıcı açıklayıcı bir ileti görür (uçlar zaten 403 döner).
 */
function PlatformLayout(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const me = useMe();
  const platform = usePlatformMe();

  if (platform.isPending || !me.data) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Spinner label={t("common.loading")} />
      </div>
    );
  }
  const signOut = () => void logout().then(() => navigate({ to: "/giris" }));
  const denied = platform.isError && isApiError(platform.error, "PLATFORM_ACCESS_REQUIRED");
  const permissions = new Set<string>(platform.data?.permissions ?? []);
  const items = NAV.filter((item) => permissions.has(item.permission));

  return (
    <StepUpProvider>
      <a href="#platform-main" className="skip-link">
        {t("shell.skipToContent")}
      </a>
      <div className="flex min-h-dvh flex-col bg-muted/30">
        <header className="border-b bg-card print:hidden">
          <div className="flex w-full flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
            <div className="flex items-center gap-2">
              <Gauge className="size-6 text-primary" aria-hidden="true" />
              <span className="font-semibold">{PRODUCT_NAME}</span>
              <span className="text-sm text-muted-foreground">· {t("platform.title")}</span>
            </div>
            <div className="ms-auto flex flex-wrap items-center gap-2">
              {platform.data ? (
                <StatusBadge tone="info">{PLATFORM_ROLE_LABELS[platform.data.role]}</StatusBadge>
              ) : null}
              <span className="text-sm">{me.data.user.fullName}</span>
              {me.data.memberships.length > 0 ? (
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/">{t("platform.toTenantPanel")}</Link>
                </Button>
              ) : null}
              <Button variant="outline" size="sm" onClick={signOut}>
                <LogOut aria-hidden="true" />
                {t("shell.userMenu.logout")}
              </Button>
            </div>
          </div>
        </header>
        {denied ? (
          <main id="platform-main" tabIndex={-1} className="mx-auto w-full max-w-3xl flex-1 p-6 outline-none">
            <EmptyState
              icon={<ShieldOff />}
              title={t("platform.deniedTitle")}
              description={t("platform.deniedDescription")}
              action={
                <Button asChild>
                  <Link to="/">{t("platform.toTenantPanel")}</Link>
                </Button>
              }
            />
          </main>
        ) : (
          <div className="flex flex-1 flex-col lg:flex-row">
            <nav
              aria-label={t("platform.navLabel")}
              className="border-b bg-card px-2 py-2 lg:w-56 lg:shrink-0 lg:border-e lg:border-b-0 print:hidden"
            >
              <ul className="flex flex-wrap gap-1 lg:flex-col">
                {items.map((item) => (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      activeOptions={{ exact: item.exact === true }}
                      className="flex min-h-10 items-center gap-2 rounded-md px-3 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground data-[status=active]:bg-primary/10 data-[status=active]:text-foreground"
                    >
                      <item.icon className="size-4" aria-hidden />
                      {t(item.labelKey)}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
            <main id="platform-main" tabIndex={-1} className="min-w-0 flex-1 p-4 outline-none sm:p-6">
              {platform.isError && !denied ? (
                platform.error && isApiError(platform.error, "MFA_SETUP_REQUIRED") ? null : (
                  <Alert tone="danger">{t("platform.loadFailed")}</Alert>
                )
              ) : null}
              {platform.data?.mfaRequired ? (
                <Alert tone="warning" title={t("platform.mfaRequiredTitle")} className="mb-4">
                  <p>{t("platform.mfaRequiredDescription")}</p>
                  <Button asChild size="sm" className="mt-2">
                    <Link to="/ayarlar/guvenlik">{t("shell.mfaRequired.action")}</Link>
                  </Button>
                </Alert>
              ) : (
                <div className="mx-auto grid w-full max-w-7xl gap-6">
                  <Outlet />
                </div>
              )}
            </main>
          </div>
        )}
      </div>
    </StepUpProvider>
  );
}
